import { useEffect, useState } from "react";
import { StyledTableBisManualPag } from "@/components/styled/StyledTableBisManualPag";
import { InvoiceDeletionModal } from "../invoiceDeletionModal";
import usePagination from "@/hooks/useSyncedPagination";
import { useInvoiceToBeValidatedTableColumns } from "./invoiceToBeValidatedTableColumns";
import { useTranslation } from "react-i18next";
import { InvoiceDto } from "@/types/api/api-types";
import { InvoiceToBeValidatedFilter } from "./invoiceToBeValidatedFilter";
import { InvoiceToBeValidatedBatchValidationModal } from "./invoiceToBeValidatedBatchValidationModal";
import { InvoiceEditModal } from "../invoiceEditModal";
import ShareIcon from "@/assets/icons/share.svg?react";
import { useGetInvoiceBoV1 } from "@/api/invoice";
import { Button } from "@/components/ui/button";
import { InvoiceUploadModal } from "@/components/backOffice/clients/invoices/createInvoiceImportModal";
import { userAtom } from "@/stores/authStore";
import { useAtomValue } from "jotai";
import { useDisplayDocument } from "@/hooks/useDisplayDocument";
import { InvoiceStatusEnum } from "@/constants/enums/invoiceStatus.enum";

type InvoiceTableProps = {
  companyId?: string;
};

export const InvoiceToBeValidatedTable = ({ companyId }: InvoiceTableProps) => {
  const { t } = useTranslation("boInvoices");
  const [searchField, setSearchField] = useState<string>("");
  const [pagination, setPagination] = usePagination("invoiceToBeValidatedBo");
  const [invoiceDeleteModal, setInvoiceDeleteModal] = useState<string | null>(null);
  const [invoiceToEdit, setInvoiceToEdit] = useState<InvoiceDto | null>(null);
  const [isBatchValidationModalOpen, setIsBatchValidationModalOpen] = useState<boolean>(false);
  const [isCreateInvoiceModalOpen, setIsCreateInvoiceModalOpen] = useState<boolean>(false);

  const user = useAtomValue(userAtom);

  const { displayDocument } = useDisplayDocument();

  const { data: invoices } = useGetInvoiceBoV1({
    invoiceId: searchField ? searchField : undefined,
    businessName: searchField ? searchField : undefined,
    status: [InvoiceStatusEnum.PENDING],
    companyId: companyId || undefined,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [setPagination, searchField, companyId]);

  const columns = useInvoiceToBeValidatedTableColumns(
    displayDocument,
    user?.role || null,
    setInvoiceToEdit,
    setInvoiceDeleteModal,
  );

  return (
    <>
      <div className="flex w-full flex-col justify-between gap-4 md:flex-row">
        <InvoiceToBeValidatedFilter searchField={searchField} setSearchField={setSearchField} />
        {!companyId && (
          <div className="flex w-fit flex-row items-stretch gap-2">
            <Button onClick={() => setIsCreateInvoiceModalOpen(true)}>
              {t("toBeValidatedTable.createInvoice")}
            </Button>
            <Button
              onClick={() => setIsBatchValidationModalOpen(true)}
              disabled={!invoices?.data.length}
              variant="outline"
              className="[&_svg]:size-6"
            >
              <ShareIcon />
            </Button>
          </div>
        )}
      </div>
      <StyledTableBisManualPag
        data={invoices?.data || []}
        columns={columns}
        setPagination={setPagination}
        pagination={pagination}
        rowCount={invoices?.count || 0}
      />
      {invoiceToEdit && (
        <InvoiceEditModal closeModal={() => setInvoiceToEdit(null)} invoice={invoiceToEdit} />
      )}
      {isCreateInvoiceModalOpen && (
        <InvoiceUploadModal closeModal={() => setIsCreateInvoiceModalOpen(false)} />
      )}
      <InvoiceDeletionModal
        closeModal={() => setInvoiceDeleteModal(null)}
        id={invoiceDeleteModal}
      />
      <InvoiceToBeValidatedBatchValidationModal
        closeModal={() => setIsBatchValidationModalOpen(false)}
        isBatchValidationModalOpen={isBatchValidationModalOpen}
      />
    </>
  );
};
